<template>
    <modal @close="onClose" ref="modal" size="lg">
        <template v-slot:title>TCP+ Bulk Sync — {{ integration.label }}</template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="!taskId">
                <p class="text-muted small">
                    Reconcile every active Employee in a Company or Store against this TCP+ integration.
                    Use Dry Run to preview the diff without writing to TC+.
                </p>

                <div class="form-row pb-2">
                    <div class="col">
                        <div class="form-check">
                            <input
                                id="bulkSyncScopeCompany"
                                name="bulkSyncScope"
                                type="radio"
                                class="form-check-input"
                                :checked="formData.scope === 'company'"
                                @click="formData.scope = 'company'"
                            />
                            <label class="form-check-label" for="bulkSyncScopeCompany">Whole Company</label>
                        </div>
                        <div class="form-check">
                            <input
                                id="bulkSyncScopeStore"
                                name="bulkSyncScope"
                                type="radio"
                                class="form-check-input"
                                :checked="formData.scope === 'store'"
                                @click="formData.scope = 'store'"
                            />
                            <label class="form-check-label" for="bulkSyncScopeStore">Single Store</label>
                        </div>
                    </div>
                </div>

                <div class="form-row pb-2" v-if="formData.scope === 'company'">
                    <div class="col">
                        <form-input v-model="formData.company_id" type="select" label="Company" :options="companyOptions" :errors="errors.company_id" />
                    </div>
                </div>

                <div class="form-row pb-2" v-if="formData.scope === 'store'">
                    <div class="col">
                        <form-input v-model="formData.store_id" type="select" label="Store" :options="storeOptions" :errors="errors.store_id" />
                    </div>
                </div>

                <div class="form-row pb-2">
                    <div class="col">
                        <form-input v-model="formData.limit" type="number" label="Limit (optional)" :helper-text="'Cap the number of EEs processed. Leave blank for all.'" :errors="errors.limit" />
                    </div>
                </div>

                <div class="form-row pb-2">
                    <div class="col">
                        <label class="d-block">
                            <input type="checkbox" v-model="formData.dry_run" />
                            Dry Run (no writes to TCP+)
                        </label>
                    </div>
                </div>
            </div>

            <div v-if="taskId">
                <p>
                    <strong>State:</strong> {{ statusPayload.state }}
                    <span v-if="statusPayload.ready"> — finished</span>
                    <span v-else> — polling every {{ pollIntervalMs / 1000 }}s</span>
                </p>

                <div v-if="statusPayload.result" class="card mb-3">
                    <div class="card-body py-2">
                        <h5 class="mb-2">Results</h5>
                        <p class="mb-1">Scope: <code>{{ statusPayload.result.scope }}</code></p>
                        <p class="mb-1">Processed: {{ statusPayload.result.processed }} / {{ statusPayload.result.total }}</p>
                        <p class="mb-1">EJCs created: {{ statusPayload.result.created_count }}</p>
                        <p class="mb-1">EJCs updated: {{ statusPayload.result.updated_count }}</p>
                        <p class="mb-1">EJCs deactivated: {{ statusPayload.result.deactivated_count }}</p>
                        <p class="mb-1">EEs with errors: {{ statusPayload.result.employees_with_errors }}</p>
                        <p v-if="statusPayload.result.dry_run" class="text-warning mb-0">(Dry run — no changes applied)</p>
                        <ul v-if="statusPayload.result.errors && statusPayload.result.errors.length" class="mt-2 mb-0">
                            <li v-for="(err, i) in statusPayload.result.errors" :key="i" class="text-danger small">{{ err }}</li>
                        </ul>
                    </div>
                </div>

                <h5 class="mb-1">Sync Log (newest first)</h5>
                <pre class="border rounded p-2 small" style="max-height: 300px; overflow-y: auto;">{{ logTailText }}</pre>
            </div>
        </slot>

        <template v-slot:footer>
            <div class="form-row" v-if="!taskId">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                </div>
                <div class="col">
                    <button type="submit" class="btn btn-block btn-primary" @click.prevent="onSubmit()" :disabled="submitting">
                        {{ submitting ? 'Queueing…' : (formData.dry_run ? 'Dry-Run Sync' : 'Start Bulk Sync') }}
                    </button>
                </div>
            </div>
            <div class="form-row" v-if="taskId">
                <div class="col">
                    <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Close</button>
                </div>
                <div class="col" v-if="statusPayload.ready">
                    <button type="button" class="btn btn-block btn-primary" @click.prevent="resetForm()">Run Another</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import api from '@/api'
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    mixins: [FormMixin, ModalMixin],
    props: ['client', 'integration'],
    data() {
        return {
            submitting: false,
            companies: [],
            stores: [],
            taskId: null,
            startedAt: null,
            statusPayload: {},
            pollIntervalMs: 3000,
            pollTimer: null,
            formData: this.makeFormData(),
            formRules: {},
        }
    },
    computed: {
        companyOptions() {
            const list = Array.isArray(this.companies) ? this.companies : []
            return [{text: '— select —', value: ''}].concat(
                list.map(c => ({text: `${c.company_code} — ${c.name || ''}`, value: c.id}))
            )
        },
        storeOptions() {
            const list = Array.isArray(this.stores) ? this.stores : []
            return [{text: '— select —', value: ''}].concat(
                list.map(s => ({
                    text: `${s.StoreAltID || '(no store_alt)'} — ${s.WorkLocation || ''} (${s.CompanyCode || ''})`,
                    value: s.id,
                }))
            )
        },
        logTailText() {
            if (!this.statusPayload.log_tail) return ''
            return this.statusPayload.log_tail.map(e => `[${e.dt}] ${e.msg}`).join('\n')
        },
    },
    methods: {
        makeFormData() {
            return {
                scope: 'company',
                company_id: '',
                store_id: '',
                limit: null,
                dry_run: true,
            }
        },
        reset() {
            this.resetForm()
            this.loadScopeData()
        },
        resetForm() {
            this.taskId = null
            this.startedAt = null
            this.statusPayload = {}
            this.formData = this.makeFormData()
            this.submitting = false
            this.stopPolling()
        },
        loadScopeData() {
            api.get(`/admin/clients/${this.client.id}/companies`).then(resp => {
                this.companies = Array.isArray(resp) ? resp : (resp && resp.data) || []
            }).catch(() => {
                this.companies = []
            })
            api.get(`/admin/clients/${this.client.id}/config/stores`).then(resp => {
                // /config/stores returns { data: [{id, Store, StoreAltID, WorkLocation, CompanyCode, ...}], warnings: [...] }
                this.stores = (resp && Array.isArray(resp.data)) ? resp.data : []
            }).catch(() => {
                this.stores = []
            })
        },
        onSubmit() {
            const payload = {
                dry_run: !!this.formData.dry_run,
            }
            if (this.formData.limit) payload.limit = parseInt(this.formData.limit, 10)
            if (this.formData.scope === 'company') {
                if (!this.formData.company_id) {
                    this.formErrors = ['Please select a Company.']
                    return
                }
                payload.company_id = this.formData.company_id
            } else {
                if (!this.formData.store_id) {
                    this.formErrors = ['Please select a Store.']
                    return
                }
                payload.store_id = this.formData.store_id
            }
            this.submitting = true
            const url = `/admin/posintegrations/${this.integration.id}/tcpplus/bulk-sync`
            api.post(url, payload).then(resp => {
                this.taskId = resp.task_id
                this.startedAt = resp.started_at || null
                this.submitting = false
                this.startPolling()
            }).catch(errors => {
                this.formErrors = errors.__all__ || ['Failed to start bulk sync.']
                this.submitting = false
            })
        },
        startPolling() {
            this.pollOnce()
            this.pollTimer = setInterval(() => this.pollOnce(), this.pollIntervalMs)
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
            }
        },
        pollOnce() {
            if (!this.taskId) return
            let url = `/admin/posintegrations/${this.integration.id}/tcpplus/bulk-sync/${this.taskId}`
            if (this.startedAt) {
                url += `?since=${encodeURIComponent(this.startedAt)}`
            }
            api.get(url).then(resp => {
                this.statusPayload = resp
                if (resp.ready) {
                    this.stopPolling()
                }
            }).catch(() => {
                // Transient errors during polling are non-fatal.
            })
        },
        onClose() {
            this.stopPolling()
            this.$emit('close')
        },
    },
    beforeUnmount() {
        this.stopPolling()
    },
}
</script>
